import { useEffect, useState } from "react";

/**
 * useYoast({ wpUrl, endpoint = "posts", slug, post, cache = true })
 * - If `post` contains `yoast_head_json`, returns that immediately.
 * - Otherwise fetches from `${wpUrl}/wp-json/wp/v2/${endpoint}?slug=...`
 * - Returns { seo, loading, error }
 */
export default function useYoast({ wpUrl, endpoint = "posts", slug, post = null, cache = true }) {
  const [seo, setSeo] = useState(() => post?.yoast_head_json ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let canceled = false;
    const base = wpUrl ? wpUrl.replace(/\/$/, "") : "";

    // If post already has seo object, use it
    if (post?.yoast_head_json) {
      setSeo(post.yoast_head_json);
      setLoading(false);
      return () => { canceled = true; };
    }

    if (!wpUrl || !slug) {
      // nothing to fetch
      setSeo(null);
      setLoading(false);
      return () => { canceled = true; };
    }

    const cacheKey = `yoast:${endpoint}:${slug}`;

    async function fetchSeo() {
      setLoading(true);
      setError(null);

      try {
        if (cache) {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (!canceled) {
              setSeo(parsed);
              setLoading(false);
              return;
            }
          }
        }

        const url = `${base}/wp-json/wp/v2/${endpoint}?slug=${encodeURIComponent(slug)}&_embed`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Yoast fetch failed (${res.status})`);
        const arr = await res.json();
        const found = Array.isArray(arr) && arr.length ? arr[0] : null;
        const yoast = found?.yoast_head_json ?? found?.yoast_head ?? null;

        if (!canceled) {
          setSeo(yoast);
          if (cache && yoast) {
            try { sessionStorage.setItem(cacheKey, JSON.stringify(yoast)); } catch (e) {}
          }
        }
      } catch (e) {
        if (!canceled) setError(e);
      } finally {
        if (!canceled) setLoading(false);
      }
    }

    fetchSeo();

    return () => { canceled = true; };
  }, [wpUrl, endpoint, slug, post, cache]);

  return { seo, loading, error };
}
